import { Add } from '@mui/icons-material';
import { Box, Skeleton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';

import { Button } from '@/components/Button';
import { useStopwatchStore } from '@/configs/store';
import { StopwatchCard } from '@/features/stopwatches/ui/StopwatchCard';
import { StopwatchFinishDialog } from '@/features/stopwatches/ui/StopwatchFinishDialog';
import { Stopwatch } from '@/types';

const StopwatchesPageContent = observer(() => {
  const store = useStopwatchStore();
  const [isCreating, setIsCreating] = useState(false);
  const [finishTarget, setFinishTarget] = useState<Stopwatch | null>(null);
  const [wasRunning, setWasRunning] = useState(false);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    const created = await store.createStopwatch({});

    if (created) {
      await store.startStopwatch(created.id);
    }

    setIsCreating(false);
  }, [store]);

  const handleFinish = useCallback((stopwatch: Stopwatch, running: boolean) => {
    setFinishTarget(stopwatch);
    setWasRunning(running);
  }, []);

  const handleFinishClose = useCallback(
    async (submitted?: boolean) => {
      if (!submitted && wasRunning && finishTarget) {
        await store.startStopwatch(finishTarget.id);
      }

      setFinishTarget(null);
      setWasRunning(false);
    },
    [wasRunning, finishTarget, store],
  );

  const isLoading = store.loadingStage.isLoading && store.stopwatches.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 3,
        pb: 0,
        maxHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" fontWeight="bold">
          Секундомеры
        </Typography>
        <Button onClick={handleCreate} disabled={isCreating} startIcon={<Add />}>
          {isCreating ? 'Создание...' : 'Создать'}
        </Button>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 2,
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={280} sx={{ borderRadius: '16px' }} />
          ))}
        </Box>
      ) : store.stopwatches.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Нет активных секундомеров
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Создайте секундомер для отслеживания времени
          </Typography>
          <Button sx={{ mt: 2 }} onClick={handleCreate} disabled={isCreating} startIcon={<Add />}>
            {isCreating ? 'Создание...' : 'Создать секундомер'}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 2,
            pb: 3,
            overflow: 'auto',
          }}
        >
          {store.stopwatches.map((sw) => (
            <StopwatchCard key={sw.id} stopwatch={sw} onFinish={handleFinish} />
          ))}
        </Box>
      )}

      <StopwatchFinishDialog
        stopwatch={finishTarget}
        open={!!finishTarget}
        onClose={handleFinishClose}
      />
    </Box>
  );
});

export const StopwatchesPage = () => <StopwatchesPageContent />;
